import { getSupabase } from '../../components/supabaseClient';
import { tableName } from '../../components/env';

const CHUNK = 200;

export function keyForRow(row, keyType) {
  if (keyType === 'EHR_ID') {
    const v = row.EHR_ID;
    return v ? String(v).trim().toLowerCase() : null;
  }
  const n = row.patient_name;
  const d = row.dob;
  if (!n || !d) return null;
  return `${String(n).trim().toLowerCase()}|${String(d).trim().slice(0, 10)}`;
}

export function findInFileDuplicates(rows, keyType) {
  const seen = new Set();
  const dup = new Set();
  rows.forEach((row, i) => {
    const k = keyForRow(row, keyType);
    if (k == null) return;
    if (seen.has(k)) dup.add(i);
    else seen.add(k);
  });
  return dup;
}

export function findExistingDuplicates(rows, keyType, existingKeys) {
  const dup = new Set();
  rows.forEach((row, i) => {
    const k = keyForRow(row, keyType);
    if (k && existingKeys.has(k)) dup.add(i);
  });
  return dup;
}

// The dedup descriptor lets the caller point the existing-key lookup at the
// chosen target: notes-c uses the case-quoted "EHR_ID" column, patients_with_ehrid
// uses the lowercase ehr_id column. Defaults preserve the original notes-c behavior.
export async function fetchExistingKeys(
  rows, keyType, getClient = getSupabase,
  { table = tableName, idColumn = 'EHR_ID', idSelect = '"EHR_ID"' } = {},
) {
  const client = await getClient();
  const existing = new Set();

  if (keyType === 'EHR_ID') {
    const ids = [...new Set(rows.map((r) => r.EHR_ID).filter(Boolean).map((v) => String(v).trim()))];
    for (let i = 0; i < ids.length; i += CHUNK) {
      const chunk = ids.slice(i, i + CHUNK);
      const { data, error } = await client.from(table).select(idSelect).in(idColumn, chunk);
      if (!error && data) data.forEach((d) => { const v = d[idColumn]; if (v != null) existing.add(String(v).trim().toLowerCase()); });
    }
  } else {
    const names = [...new Set(rows.map((r) => r.patient_name).filter(Boolean).map((v) => String(v).trim()))];
    for (let i = 0; i < names.length; i += CHUNK) {
      const chunk = names.slice(i, i + CHUNK);
      const { data, error } = await client.from(table).select('patient_name,dob').in('patient_name', chunk);
      if (!error && data) data.forEach((d) => { if (d.patient_name && d.dob) existing.add(`${String(d.patient_name).trim().toLowerCase()}|${String(d.dob).trim().slice(0, 10)}`); });
    }
  }
  return existing;
}

// Fetch the FULL current rows for the file's would-be duplicates, keyed by the same
// normalized key keyForRow() produces, so the caller can diff/merge against them.
// EHR_ID mode selects by the id column in chunks; name_dob mode selects by name and
// keys by name|dob. Rows the query can't key (missing id / name+dob) are omitted.
export async function fetchExistingRows(
  rows, keyType, getClient = getSupabase,
  { table = tableName, idColumn = 'EHR_ID' } = {},
) {
  const client = await getClient();
  const map = new Map();
  if (keyType === 'EHR_ID') {
    const ids = [...new Set(rows.map((r) => r.EHR_ID).filter(Boolean).map((v) => String(v).trim()))];
    for (let i = 0; i < ids.length; i += CHUNK) {
      const chunk = ids.slice(i, i + CHUNK);
      const { data, error } = await client.from(table).select('*').in(idColumn, chunk);
      if (!error && data) {
        data.forEach((row) => {
          const v = row[idColumn];
          if (v != null) map.set(String(v).trim().toLowerCase(), row);
        });
      }
    }
  } else {
    const names = [...new Set(rows.map((r) => r.patient_name).filter(Boolean).map((v) => String(v).trim()))];
    for (let i = 0; i < names.length; i += CHUNK) {
      const chunk = names.slice(i, i + CHUNK);
      const { data, error } = await client.from(table).select('*').in('patient_name', chunk);
      if (!error && data) {
        data.forEach((row) => {
          if (row.patient_name && row.dob) {
            map.set(`${String(row.patient_name).trim().toLowerCase()}|${String(row.dob).trim().slice(0, 10)}`, row);
          }
        });
      }
    }
  }
  return map;
}
